import { Schema } from "effect";

// Projected read model derived incrementally from the append-only ChatRow log.
// This model lives in @workerstation/shared as a pure, DOM-free state model.

import { ChatAttachment, type ChatRow, ChatTurnEndReason, type ChatProvisional } from "./chat-contract.ts";

export const ChatToolStatus = /* @__PURE__ */ (() => Schema.Literals(["running", "awaiting_approval", "denied", "completed", "error"]))();
export type ChatToolStatus = typeof ChatToolStatus.Type;

export const ChatTextPart = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("text"),
  text: Schema.String,
  transient: Schema.Boolean.pipe(Schema.optional),
}))();
export type ChatTextPart = typeof ChatTextPart.Type;

export const ChatReasoningPart = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("reasoning"),
  text: Schema.String,
  transient: Schema.Boolean.pipe(Schema.optional),
}))();
export type ChatReasoningPart = typeof ChatReasoningPart.Type;

export const ChatToolPart = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("tool"),
  callId: Schema.String,
  tool: Schema.String,
  args: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)),
  status: ChatToolStatus,
  autoApproved: Schema.Boolean.pipe(Schema.optional),
  approvalId: Schema.String.pipe(Schema.optional),
  destructive: Schema.Boolean.pipe(Schema.optional),
  resultText: Schema.String.pipe(Schema.optional),
}))();
export type ChatToolPart = typeof ChatToolPart.Type;

export const ChatProposalPart = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("proposal"),
  proposalId: Schema.String,
  worktreeId: Schema.String,
  summary: Schema.String,
  fileCount: Schema.Number,
  pending: Schema.Boolean,
  outcome: Schema.Literals(["merged", "discarded", "merge_failed", "discard_failed"]).pipe(Schema.optional),
  detail: Schema.String.pipe(Schema.optional),
}))();
export type ChatProposalPart = typeof ChatProposalPart.Type;

export const ChatPart = /* @__PURE__ */ (() => Schema.Union([ChatTextPart, ChatReasoningPart, ChatToolPart, ChatProposalPart]))();
export type ChatPart = typeof ChatPart.Type;

export const ChatUserMessage = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String,
  role: Schema.Literal("user"),
  text: Schema.String,
  attachments: Schema.Array(ChatAttachment).pipe(Schema.optional),
}))();
export type ChatUserMessage = typeof ChatUserMessage.Type;

export const ChatTurnEndInfo = /* @__PURE__ */ (() => Schema.Struct({
  reason: ChatTurnEndReason,
  detail: Schema.String.pipe(Schema.optional),
}))();
export type ChatTurnEndInfo = typeof ChatTurnEndInfo.Type;

export const ChatAssistantMessage = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String,
  role: Schema.Literal("assistant"),
  parts: Schema.Array(ChatPart),
  open: Schema.Boolean,
  turnEnd: ChatTurnEndInfo.pipe(Schema.optional),
}))();
export type ChatAssistantMessage = typeof ChatAssistantMessage.Type;

export const ChatMessage = /* @__PURE__ */ (() => Schema.Union([ChatUserMessage, ChatAssistantMessage]))();
export type ChatMessage = typeof ChatMessage.Type;

export const ChatSessionState = /* @__PURE__ */ (() => Schema.Struct({
  messages: Schema.Array(ChatMessage),
  appliedSequence: Schema.Number,
  streamGeneration: Schema.Number.pipe(Schema.optional),
}))();
export type ChatSessionState = typeof ChatSessionState.Type;

export function emptyChatSession(): ChatSessionState {
  return {
    messages: [],
    appliedSequence: 0,
  };
}

export function applyChatRow(state: ChatSessionState, row: ChatRow): ChatSessionState {
  if (row.sequence <= state.appliedSequence) {
    return state;
  }

  const messages = [...state.messages];

  function getOrOpenAssistantMessage(): { msg: ChatAssistantMessage; msgIndex: number } {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === "assistant" && lastMsg.open) {
      return { msg: lastMsg, msgIndex: messages.length - 1 };
    }
    const newMsg: ChatAssistantMessage = {
      id: `assistant-${row.sequence}`,
      role: "assistant",
      parts: [],
      open: true,
    };
    messages.push(newMsg);
    return { msg: newMsg, msgIndex: messages.length - 1 };
  }

  if (row.kind === "user") {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === "assistant" && lastMsg.open) {
      messages[messages.length - 1] = {
        ...lastMsg,
        open: false,
      };
    }
    messages.push({
      id: `user-${row.sequence}`,
      role: "user",
      text: row.text,
      ...(row.attachments?.length ? { attachments: row.attachments } : {}),
    });
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "assistant") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    const newPart: ChatTextPart = { kind: "text", text: row.text };
    messages[msgIndex] = {
      ...msg,
      parts: [...msg.parts, newPart],
    };
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "tool_call") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    const newPart: ChatToolPart = {
      kind: "tool",
      callId: row.callId,
      tool: row.tool,
      args: row.args,
      status: "running",
      ...(row.autoApproved ? { autoApproved: true } : {}),
    };
    messages[msgIndex] = {
      ...msg,
      parts: [...msg.parts, newPart],
    };
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "approval_request") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    const partIndex = msg.parts.findIndex(
      (p) => p.kind === "tool" && p.callId === row.callId
    );
    if (partIndex >= 0) {
      const existing = msg.parts[partIndex] as ChatToolPart;
      const updatedPart: ChatToolPart = {
        ...existing,
        status: existing.status === "denied" ? "denied" : "awaiting_approval",
        approvalId: row.approvalId,
        destructive: row.destructive,
      };
      const newParts = [...msg.parts];
      newParts[partIndex] = updatedPart;
      messages[msgIndex] = { ...msg, parts: newParts };
    } else {
      const newPart: ChatToolPart = {
        kind: "tool",
        callId: row.callId,
        tool: row.tool,
        args: row.args,
        status: "awaiting_approval",
        approvalId: row.approvalId,
        destructive: row.destructive,
      };
      messages[msgIndex] = { ...msg, parts: [...msg.parts, newPart] };
    }
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "approval_decision") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    const findInMsg = (m: ChatAssistantMessage) =>
      m.parts.findIndex(
        (p) =>
          p.kind === "tool" &&
          (p.approvalId === row.approvalId ||
            (row.callId !== undefined && p.callId === row.callId))
      );

    let targetMsg = msg;
    let targetMsgIndex = msgIndex;
    let foundIndex = findInMsg(msg);

    if (foundIndex < 0) {
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role === "assistant") {
          const idx = findInMsg(m);
          if (idx >= 0) {
            targetMsg = m;
            targetMsgIndex = i;
            foundIndex = idx;
            break;
          }
        }
      }
    }

    if (foundIndex >= 0 && targetMsg) {
      const existing = targetMsg.parts[foundIndex] as ChatToolPart;
      const updatedPart: ChatToolPart = {
        ...existing,
        approvalId: existing.approvalId ?? row.approvalId,
        ...(row.autoApproved ? { autoApproved: true } : {}),
        status: !row.approved
          ? "denied"
          : existing.status === "awaiting_approval"
            ? "running"
            : existing.status,
      };
      const newParts = [...targetMsg.parts];
      newParts[foundIndex] = updatedPart;
      messages[targetMsgIndex] = { ...targetMsg, parts: newParts };
    }
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "tool_result") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    let targetMsg = msg;
    let targetMsgIndex = msgIndex;
    let foundIndex = -1;

    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant") {
        const idx = m.parts.findIndex(
          (p) => p.kind === "tool" && p.callId === row.callId
        );
        if (idx >= 0) {
          targetMsg = m;
          targetMsgIndex = i;
          foundIndex = idx;
          break;
        }
      }
    }

    if (foundIndex >= 0 && targetMsg) {
      const existing = targetMsg.parts[foundIndex] as ChatToolPart;
      const status: ChatToolStatus =
        existing.status === "denied"
          ? "denied"
          : row.isError
          ? "error"
          : "completed";

      const updatedPart: ChatToolPart = {
        ...existing,
        status,
        resultText: row.resultText,
      };
      const newParts = [...targetMsg.parts];
      newParts[foundIndex] = updatedPart;
      messages[targetMsgIndex] = { ...targetMsg, parts: newParts };
    } else {
      const newPart: ChatToolPart = {
        kind: "tool",
        callId: row.callId,
        tool: row.tool,
        args: {},
        status: row.isError ? "error" : "completed",
        resultText: row.resultText,
      };
      messages[msgIndex] = { ...msg, parts: [...msg.parts, newPart] };
    }

    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "proposal") {
    const { msg, msgIndex } = getOrOpenAssistantMessage();
    const newPart: ChatProposalPart = {
      kind: "proposal",
      proposalId: row.proposalId,
      worktreeId: row.worktreeId,
      summary: row.summary,
      fileCount: row.fileCount,
      pending: true,
    };
    messages[msgIndex] = {
      ...msg,
      parts: [...msg.parts, newPart],
    };
    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "proposal_outcome") {
    let targetMsg: ChatAssistantMessage | undefined;
    let targetMsgIndex = -1;
    let foundIndex = -1;

    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant") {
        const idx = m.parts.findIndex(
          (p) => p.kind === "proposal" && p.proposalId === row.proposalId
        );
        if (idx >= 0) {
          targetMsg = m;
          targetMsgIndex = i;
          foundIndex = idx;
          break;
        }
      }
    }

    if (foundIndex >= 0 && targetMsg) {
      const existing = targetMsg.parts[foundIndex] as ChatProposalPart;
      const updatedPart: ChatProposalPart = {
        ...existing,
        pending: false,
        outcome: row.outcome,
        detail: row.detail,
      };
      const newParts = [...targetMsg.parts];
      newParts[foundIndex] = updatedPart;
      messages[targetMsgIndex] = { ...targetMsg, parts: newParts };
    } else {
      const { msg, msgIndex } = getOrOpenAssistantMessage();
      const newPart: ChatProposalPart = {
        kind: "proposal",
        proposalId: row.proposalId,
        worktreeId: "",
        summary: "",
        fileCount: 0,
        pending: false,
        outcome: row.outcome,
        detail: row.detail,
      };
      messages[msgIndex] = { ...msg, parts: [...msg.parts, newPart] };
    }

    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  if (row.kind === "turn_end") {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === "assistant" && lastMsg.open) {
      if (row.reason === "awaiting_approval") {
        // Leave open
      } else {
        messages[messages.length - 1] = {
          ...lastMsg,
          open: false,
          turnEnd: {
            reason: row.reason,
            detail: row.detail,
          },
        };
      }
    } else if (row.reason !== "awaiting_approval") {
      messages.push({
        id: `assistant-${row.sequence}`,
        role: "assistant",
        parts: [],
        open: false,
        turnEnd: {
          reason: row.reason,
          detail: row.detail,
        },
      });
    }

    return {
      messages,
      appliedSequence: row.sequence,
      streamGeneration: state.streamGeneration,
    };
  }

  return state;
}

export function applyChatProvisional(
  state: ChatSessionState,
  provisional: ChatProvisional | null,
  streamGeneration?: number
): ChatSessionState {
  const genChanged =
    streamGeneration !== undefined &&
    state.streamGeneration !== undefined &&
    streamGeneration !== state.streamGeneration;

  const hasProvisionalContent =
    !genChanged &&
    provisional &&
    provisional.turnActive &&
    (Boolean(provisional.text) || Boolean(provisional.reasoning));

  const cleanMessages = state.messages.map((msg) => {
    if (msg.role !== "assistant") return msg;
    const nonTransientParts = msg.parts.filter((p) => !("transient" in p && p.transient));
    if (nonTransientParts.length === msg.parts.length) return msg;
    return { ...msg, parts: nonTransientParts };
  });

  if (!hasProvisionalContent) {
    return {
      messages: cleanMessages.filter(
        (msg) =>
          !(
            msg.role === "assistant" &&
            msg.id === "assistant-provisional" &&
            msg.parts.length === 0
          )
      ),
      appliedSequence: state.appliedSequence,
      streamGeneration: streamGeneration ?? state.streamGeneration,
    };
  }

  const messages = [...cleanMessages];
  let openMsgIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant" && (messages[i] as ChatAssistantMessage).open) {
      openMsgIndex = i;
      break;
    }
  }

  if (openMsgIndex < 0) {
    const newMsg: ChatAssistantMessage = {
      id: `assistant-provisional`,
      role: "assistant",
      parts: [],
      open: true,
    };
    messages.push(newMsg);
    openMsgIndex = messages.length - 1;
  }

  const openMsg = messages[openMsgIndex] as ChatAssistantMessage;
  const newParts = [...openMsg.parts];

  const durableText = newParts
    .filter((p): p is ChatTextPart => p.kind === "text" && !p.transient)
    .map((p) => p.text)
    .join("");

  if (provisional.reasoning) {
    newParts.push({
      kind: "reasoning",
      text: provisional.reasoning,
      transient: true,
    });
  }

  if (provisional.text) {
    let provTextToAdd = provisional.text;
    if (durableText && provTextToAdd.startsWith(durableText)) {
      provTextToAdd = provTextToAdd.slice(durableText.length);
    } else if (durableText && provTextToAdd === durableText) {
      provTextToAdd = "";
    }
    if (provTextToAdd) {
      newParts.push({
        kind: "text",
        text: provTextToAdd,
        transient: true,
      });
    }
  }

  messages[openMsgIndex] = {
    ...openMsg,
    parts: newParts,
  };

  return {
    messages,
    appliedSequence: state.appliedSequence,
    streamGeneration: streamGeneration ?? state.streamGeneration,
  };
}
